import { Button } from '@src/components/button/Button.component'
import { SignupButton } from '@src/components/signup-button/SignupButton.component'
import { useAppDispatch } from '@src/hooks/useAppDispatch'
import { useTranslation } from '@src/hooks/useTranslation'
import { mixpanelService } from '@src/srv/mixpanel.service'
import { setDiscountCode, setDiscountPercentage } from '@src/store/discountCode/discountCode.slice'
import { DatoCmsFileField } from 'dato-cms-assets'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { useCallback, useEffect, useRef } from 'react'
import { NcImage } from '../nc-image/NcImage.component'
import styles from './PopupOfferCampaign.module.scss'

export interface PopupOfferCampaignProps {
  name: string
  isOpen: boolean
  _modelApiKey: 'offer_campaign_popup'
  offerTitle: string
  offerText: string
  offerButtonText: string
  offerCode: string
  discountPercentage: string
  image: DatoCmsFileField
  onDismiss: () => void
  showSingleCtaButton?: boolean
  saveDiscountForLater: boolean
  secondaryButtonText: string
}

export function PopupOfferCampaign({
  name,
  isOpen,
  offerTitle,
  offerText,
  offerButtonText,
  offerCode,
  discountPercentage,
  onDismiss,
  image,
  showSingleCtaButton = false,
  saveDiscountForLater = false,
  secondaryButtonText,
}: PopupOfferCampaignProps): ReactNode {
  const dispatch = useAppDispatch()
  const button = useRef<any>()
  const translate = useTranslation()
  const hasOpened = useRef(false)

  const onOpen = useCallback(() => {
    // Focusing an element inside the modal avoids the problem of the modal
    // appearing visually but going unnoticed by screen readers, whose focus
    // remains on the main page content behind, often with no clear path forward
    // for the user to rectify the situation.
    button.current.focus()

    hasOpened.current = true
    mixpanelService.track('PopupOfferCampaignImpression', {
      name,
    })
  }, [])

  const onClose = useCallback(() => {
    mixpanelService.track('PopupOfferCampaignClose', {
      name,
    })
  }, [])

  useEffect(() => {
    if (isOpen) {
      onOpen()
    } else if (hasOpened.current) {
      onClose()
    }
  }, [isOpen])

  const buttonProps: ButtonHTMLAttributes<HTMLButtonElement> = {
    'aria-label': translate('popup-close-button'),
    className: styles.PopupOfferCampaign__closeButton,
    onClick: onDismiss,
  }

  if (discountPercentage.length < 1) {
    discountPercentage = ''
  }

  const saveOrDismissDiscountCode = useCallback(() => {
    if (!saveDiscountForLater) {
      mixpanelService.track('PopupOfferCampaignDismissDiscountCode', { name })
      onDismiss()
      return
    }

    dispatch(setDiscountCode(offerCode, 'OfferPopup'))
    dispatch(setDiscountPercentage(discountPercentage))
    mixpanelService.track('PopupOfferCampaignSaveDiscountCode', { name })
    onDismiss()
  }, [saveDiscountForLater, name, onDismiss, dispatch, offerCode, discountPercentage])

  const onPopupSignup = useCallback(() => {
    mixpanelService.track('PopupOfferCampaignSignup', {
      name,
    })
  }, [])

  return (
    <div className={styles.PopupOfferCampaign}>
      <button {...buttonProps} ref={button}>
        <svg height={28} width={28} xmlns="http://www.w3.org/2000/svg" viewBox="-4 -4 42 42">
          <circle
            cx={18}
            cy={17.5}
            r={20}
            className={styles.PopupOfferCampaign__closeIconBackground}
          />
          <polygon
            className={styles.PopupOfferCampaign__closeIconForeground}
            points="27.661 9.366 26.325 8 18 16.63 9.675 8 8.339 9.366 16.661 18 8.339 26.634 9.675 28 18 19.37 26.325 28 27.661 26.634 19.339 18 27.661 9.366"
          />
        </svg>
      </button>
      <div className={styles.PopupOfferCampaign__image}>
        <NcImage {...image} objectFit="contain" />
      </div>
      <div className={styles.PopupOfferCampaign__content}>
        <div
          id="popup-title"
          className={styles.PopupOfferCampaign__offerTitle}
          dangerouslySetInnerHTML={{ __html: offerTitle }}
        />
        <div
          id="popup-description"
          className={styles.PopupOfferCampaign__offerText}
          dangerouslySetInnerHTML={{ __html: offerText }}
        />
        <div
          className={`${styles.PopupOfferCampaign__offerButton} ${
            showSingleCtaButton ? styles.PopupOfferCampaign__offerButton___noMargin : ''
          }`}
        >
          <SignupButton theme="brandPurple" code={offerCode} onClick={onPopupSignup}>
            {offerButtonText}
          </SignupButton>
          <br />
          {!showSingleCtaButton && (
            <Button theme="ghost" onClick={saveOrDismissDiscountCode}>
              {secondaryButtonText}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
